/**
 * Used to handle UI updates.
 *
 * File: \src\components\login\LoginStorePinia.ts
 * Created Date: Thursday, April 28th 2022, 13:33:16
 * Author: Kircheva Boryana (HAU-EDCP)
 * -----
 * Last Modified: November 22nd 2024, 07:42:34
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2019 Liebherr-Hausgeräte GmbH
 */
import { defineStore } from 'pinia';
import { RouteLocationNormalizedGeneric } from 'vue-router';

enum STORE_MODE {
  SITE,
  SOCKET
}

interface UpdateStoreState {
  manualUpdateTime: Date;
  manualUpdatePossible: boolean;
  siteUpdateTime: Date;
  socketUpdateTime: Date;
  storeMode: number;
}

/**
 * Holds the mechanism to control UI updates.
 *
 * @export
 * @class UpdateStore
 */
export const useUpdateStore = defineStore('updateStore', {
  // The store's state
  state: (): UpdateStoreState => ({
    // This is the manual update time. Components can watch it to then update themselves.
    manualUpdateTime: new Date(),
    // Is manual update available for the page or not?
    manualUpdatePossible: false,
    // The time that's updated on user navigation
    siteUpdateTime: new Date(),
    // The time that's updated on websocket events
    socketUpdateTime: new Date(),
    // The current mode, depends on the route the user is on
    storeMode: STORE_MODE.SITE
  }),

  getters: {
    /**
     * Gets the update date. This can be the "site" update on user navigation.
     * It can also be the websocket update date on the dashboard.
     *
     * @returns {Date} The last update date.
     */
    getUpdateDate(): Date {
      if (this.storeMode === STORE_MODE.SOCKET) {
        return this.socketUpdateTime;
      }

      return this.siteUpdateTime;
    },

    /**
     * Getter for the manual update time. This is bumped when the user presses the update button.
     *
     * @returns {Date} The manual update time.
     */
    getManualUpdateTime(): Date {
      return this.manualUpdateTime;
    },

    /**
     * Determines whether manual updates are possible or not.
     *
     * @returns {boolean} true if manual update should be possible.
     */
    isManualUpdatePossible(): boolean {
      return this.manualUpdatePossible;
    }
  },

  actions: {
    /**
     * Call this action to bump the site date. It is calld from the router when the route changes.
     */
    bumpSiteDate(): void {
      this.doBumpSiteDate();
    },

    /**
     * Call this to bump the websocket date. It is called by the websocket listener when an event comes in.
     */
    bumpSocketDate(): void {
      this.doBumpSocketDate();
    },

    /**
     * Triggers a manual update by changing the date.
     */
    triggerManualUpdate(): void {
      this.doManualUpdate();
    },

    /**
     * Called by the router. Notifies the store of the new route.
     *
     * @param {Route} newRoute The new route.
     */
    routeChanged(newRoute: RouteLocationNormalizedGeneric): void {
      window.scrollTo(0, 0);
      this.bumpSiteDate();

      if (newRoute.meta.updateDate === 'SOCKET') {
        this.changeStoreMode(STORE_MODE.SOCKET);
      } else {
        this.changeStoreMode(STORE_MODE.SITE);
      }

      let manualUpdateEnabled: boolean = false;

      // manualUpdateEnabled defaults to 'false'
      if (newRoute.meta.manualUpdateEnabled) {
        manualUpdateEnabled = true;
      }

      this.setManualUpdatePossible(manualUpdateEnabled);
    },

    /**
     * This changes the store's mode. There are two: A mode for
     * normal site navigation. One for websocket updates, that is used
     * when the user is watching the dashboard.
     *
     * @param {number} mode The mode of the store, either SITE or SOCKET.
     */
    changeStoreMode(mode: number) {
      this.storeMode = mode;
      this.siteUpdateTime = new Date();
      this.socketUpdateTime = new Date();
    },

    /**
     * Setter that sets the manual update possible or not.
     *
     * @param {boolean} manualPossible true if manual updates should be possible.
     */
    setManualUpdatePossible(manualPossible: boolean) {
      this.manualUpdatePossible = manualPossible;
    },

    /**
     * This bumps the site time. It is called after user navigation.
     *
     */
    doBumpSiteDate(): void {
      this.siteUpdateTime = new Date();
    },

    /**
     * This bumps the websocket time. It is called by the websocket listener when
     * an event happens.
     *
     */
    doBumpSocketDate(): void {
      this.socketUpdateTime = new Date();
    },

    /**
     * Bump the manual update time. Components can watch that change and then trigger an update.
     *
     */
    doManualUpdate(): void {
      this.manualUpdateTime = new Date();
    }
  }
});
